import {Box, useTheme} from '@mui/material';
import {useEffect, useId, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

const darkenHex = (hex: string): string => {
  const trimmed = hex.trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(trimmed)) return hex;
  const r = Math.round(parseInt(trimmed.slice(1, 3), 16) * 0.35);
  const g = Math.round(parseInt(trimmed.slice(3, 5), 16) * 0.35);
  const b = Math.round(parseInt(trimmed.slice(5, 7), 16) * 0.35);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

type MermaidBlockProps = {
  chart: string;
};

const MermaidBlock = ({chart}: MermaidBlockProps) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const id = useId().replace(/:/g, '_');
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {default: mermaid} = await import('mermaid');
        mermaid.initialize({
          startOnLoad: false,
          theme: isDark ? 'dark' : 'default',
          fontFamily: theme.typography.fontFamily
        });
        const {svg: renderedSvg} = await mermaid.render(`mermaid-${id}`, chart.trim());
        const responsiveSvg = renderedSvg
          .replace(/(<svg[^>]*?)\s+width="[^"]*"/, '$1')
          .replace(/(<svg[^>]*?)\s+height="[^"]*"/, '$1')
          .replace(/fill:(#[0-9a-fA-F]{6})\s*!important/g, (_, color) =>
            isDark ? `fill:${darkenHex(color)}` : `fill:${color}`
          );
        if (!cancelled) {
          setSvg(responsiveSvg);
          setError(null);
        }
      } catch {
        if (!cancelled) {
          setError(t('Mermaid図の描画に失敗しました'));
          setSvg('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chart, id, isDark, theme, t]);

  if (error) {
    return (
      <Box
        component="pre"
        sx={{
          my: 0.5,
          p: 1,
          borderRadius: 1,
          bgcolor: isDark ? 'grey.900' : 'grey.200',
          overflow: 'auto',
          fontSize: theme.typography.caption.fontSize
        }}
      >
        <code>{chart}</code>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        my: 1,
        overflow: 'auto',
        textAlign: 'center',
        '& svg': {
          width: '100%',
          height: 'auto',
          display: 'block',
          margin: '0 auto'
        }
      }}
      dangerouslySetInnerHTML={{__html: svg}}
    />
  );
};

export default MermaidBlock;
